// Resume-vs-create routing for the PowerSync createSession dispatch.
//
// dispatchCreateSession's resume detection reads the LOCAL mirror: a
// completed session at the tapped scope turns the tap into an in-place
// reopen. Since the per-user captures bucket (capture_items.user_id, the
// PSYNC_S2305 follow-up) the stream syncs the user's FULL session history,
// so that assumption holds whenever the mirror is caught up. The one gap
// left is MIRROR LAG: a completed session the server holds that the device
// has not received yet (fresh install, first sync still streaming). For that
// gap a local-first INSERT would 409 on upload (the server's would-reopen
// conflict gate), the connector would orphan-drop the local row, and the
// screen would bounce once — so the router first tries a BOUNDED, ONE-SHOT
// REST request (no client id → the server reopens the user's latest
// completed session or creates fresh) and falls through to local-first on
// ANY failure.
//
// The divert is best-effort, never load-bearing (Audit-2 P1 #1 / #2):
//
//   - it runs only while `isOnline()` says so, AND only when the device has
//     NO capture_sessions write still waiting in ps_crud. A REST session
//     created while a local PUT/DELETE for another session id is queued
//     would reorder against that queue: the queued PUT's 409 re-home would
//     move a CANCELLED session's items onto the fresh REST session and the
//     cancel itself would be preserved-not-redirected — resurrected counts.
//     With anything pending, local-first (the one-shot 409 bounce) is
//     strictly safer;
//   - it is a single bounded fetch (REST_DIVERT_TIMEOUT_MS), NOT the REST
//     adapter's retry queue. `navigator.onLine` reports link association,
//     not reachability: a captive portal, a dead uplink, a 5xx/429 from a
//     rolling backend, a 401 or a stalled socket all look "online" and would
//     park the queue forever — "Resuming…" with no row and no error, the
//     very hang audit-1-#1 was raised on. A rejection or timeout here simply
//     falls through;
//   - offline, or on any divert failure, the local-first INSERT always makes
//     progress and uploads on reconnect; a later would-reopen 409 converges
//     via orphan-drop + sync-down on the next tap (one-shot, never a loop,
//     now that full history syncs per user).
//
// Site-scoped creates (buildingId === null) keep the local-first path: a
// site carries no status signal to distinguish "server holds a session"
// from "never captured", and the site screen only offers Resume when a
// completed session IS in the mirror. After a cancel the LOCAL building
// status deliberately stays in_progress until the buildings stream delivers
// the server's not_started (see …-cancel-session.ts), so a post-cancel tap
// takes the divert branch — which is exactly why the pending-upload probe
// exists.
//
// HISTORY: introduced for the retired 30-day recency window
// (capture_sessions.in_sync_window, migration 0020), when an aged-out
// completed session was permanently off-device. The column is retained,
// unused.

import type { SessionSummary } from '@citycare/shared'

import type { CaptureEventBus } from './capture-events'
import type {
  CaptureMutation,
  CaptureMutationResult,
} from './capture-client.types'
import { dispatchCreateSession } from './capture-client-powersync-create-session'
import type { PowerSyncDb } from './capture-client-powersync-db'
import type { ApiFetchImpl } from './capture-client-transport'

type CreateSessionMutation = Extract<CaptureMutation, { kind: 'createSession' }>

/** Upper bound on the mirror-lag divert; beyond it local-first wins. */
export const REST_DIVERT_TIMEOUT_MS = 4_000

/**
 * True when ps_crud still holds a capture_sessions write (PUT / PATCH /
 * DELETE) that has not uploaded. The divert must not run ahead of it.
 */
export async function hasPendingSessionUpload(db: PowerSyncDb): Promise<boolean> {
  const rows = await db.getAll<{ id: string }>(
    `SELECT id FROM ps_crud
     WHERE json_extract(data, '$.type') = 'capture_sessions'
     LIMIT 1`,
  )
  return rows.length > 0
}

/**
 * ONE bounded POST /capture/sessions without a client id — never the REST
 * adapter's retry queue. Rejects on any HTTP/network failure or when
 * `timeoutMs` elapses (the fetch is aborted), so the caller can fall back.
 */
export async function restCreateSessionOnce(
  fetchImpl: ApiFetchImpl,
  mutation: CreateSessionMutation,
  timeoutMs: number = REST_DIVERT_TIMEOUT_MS,
): Promise<CaptureMutationResult> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  try {
    const session = await fetchImpl<SessionSummary>('/capture/sessions', {
      method: 'POST',
      json: { siteId: mutation.siteId, buildingId: mutation.buildingId },
      signal: controller.signal,
    })
    return { kind: 'createSession', session }
  } finally {
    clearTimeout(timer)
  }
}

export async function routeCreateSessionDispatch(
  mutation: CreateSessionMutation,
  db: PowerSyncDb,
  bus: CaptureEventBus,
  restDispatch: (m: CreateSessionMutation) => Promise<CaptureMutationResult>,
  isOnline: () => boolean,
): Promise<CaptureMutationResult> {
  const existing = await db.getAll<{ id: string }>(
    mutation.buildingId !== null
      ? `SELECT id FROM capture_sessions
         WHERE user_id = ? AND building_id = ?
         LIMIT 1`
      : `SELECT id FROM capture_sessions
         WHERE user_id = ? AND site_id = ? AND building_id IS NULL
         LIMIT 1`,
    mutation.buildingId !== null
      ? [mutation.userId, mutation.buildingId]
      : [mutation.userId, mutation.siteId],
  )
  if (existing.length > 0) {
    return dispatchCreateSession(mutation, db, bus)
  }

  if (mutation.buildingId !== null) {
    const buildingRows = await db.getAll<{ status: string }>(
      `SELECT status FROM buildings WHERE id = ? LIMIT 1`,
      [mutation.buildingId],
    )
    const status = buildingRows[0]?.status
    // Best-effort divert: ONLINE, nothing session-shaped pending in ps_crud,
    // one bounded attempt, local-first on any failure (Audit-2 P1 #1/#2).
    // lint:powersync-create-session-route-online-guard pins all three.
    if (
      status !== undefined &&
      status !== 'not_started' &&
      isOnline() &&
      !(await hasPendingSessionUpload(db))
    ) {
      try {
        return await restDispatch(mutation)
      } catch {
        // Unreachable / 5xx / 401 / timeout — the local-first path below
        // always makes progress; the upload converges on reconnect.
      }
    }
  }

  return dispatchCreateSession(mutation, db, bus)
}
